import { Manager } from "../..";
import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import {
    Filter,
    FilterMetadata,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { ManagerAssets } from "./ManagerAssets";
import { DEFAULT_ASSET_LIST_QUERY } from "~/modules/assets/consts";
import { AssetColumns } from "~/modules/assets/types";
import { ManagerHeader } from "./ManagerHeader";
import { MetricTimeseriesChart } from "~/modules/visualization/components/MetricTimeseriesChart";
import {
    Props as AggregateDisplayProps,
    AggregateGrid,
} from "~/components/AggregateDisplay";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import { HomePageLeagueTable } from "~/modules/main/pages/home/HomePageLeagueTable";
import { LeagueTablePieChart } from "~/modules/league_tables/components/LeagueTablePieChart";

// // // //

interface Props {
    manager: Manager;
    aggregates: Array<AggregateDisplayProps>;
    assetFilterMetadata: FilterMetadata[];
    chartControls: {
        hasDistributedData: boolean;
        hasRepresentedData: boolean;
        hasStablecoinsData: boolean;
    };
    leagueTableTabs: LeagueTableTabsByDistributionType;
}

/**
 * ManagerIndividualPage
 * Individual manager page component displaying KPIs and metrics for a specific manager
 */
export function ManagerIndividualPage(props: Props) {
    const {
        manager,
        aggregates,
        assetFilterMetadata,
        chartControls,
        leagueTableTabs,
    } = props;

    // Filter for this specific manager
    const managerFilters: Filter[] = [
        {
            field: TokenTimeseriesColumns.managerID,
            operator: FilterOperators.Equals,
            value: manager.id,
        },
    ];

    // Define flags to conditionally show visualization controls
    const hasDistributedData = chartControls.hasDistributedData;
    const hasRepresentedData = chartControls.hasRepresentedData;
    const hasStablecoinsData = chartControls.hasStablecoinsData;
    const onlyHasStablecoins =
        hasStablecoinsData && !hasRepresentedData && !hasDistributedData;

    return (
        <div className="flex flex-col h-full grow">
            <ManagerHeader manager={manager} />

            <AggregateGrid aggregates={aggregates} />

            <VisualizationGrid className="">
                <MetricTimeseriesChart
                    title="Manager Metrics"
                    analyticsChartName="manager-individual-metrics"
                    filters={managerFilters}
                    columnSpanConfig={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                        xxl: 12,
                    }}
                    controls={{
                        groupBy: {
                            enabled: false,
                            value: TokenTimeseriesGroupBy.asset,
                        },
                        distributionSelect: {
                            distributed: {
                                enabled: hasDistributedData,
                            },
                            represented: {
                                enabled: hasRepresentedData,
                            },
                            stablecoins: {
                                enabled:
                                    hasStablecoinsData && !onlyHasStablecoins,
                                active: onlyHasStablecoins,
                            },
                        },
                    }}
                />

                <HomePageLeagueTable
                    leagueTableTabs={leagueTableTabs}
                    analyticsTableName="manager-league-table"
                />

                <LeagueTablePieChart
                    title="Total RWA Value"
                    leagueTableTabs={leagueTableTabs}
                    defaultGroupBy={LeagueTableGroupTypes.platforms}
                    allowedGroupByValues={[
                        LeagueTableGroupTypes.platforms,
                        LeagueTableGroupTypes.assetClasses,
                        LeagueTableGroupTypes.networks,
                    ]}
                    colSpans={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 6,
                        xxl: 6,
                    }}
                />
            </VisualizationGrid>

            <ManagerAssets
                manager={manager}
                filterMetadata={assetFilterMetadata}
                assetsQuery={{
                    ...DEFAULT_ASSET_LIST_QUERY,
                    pagination: {
                        page: 1,
                        perPage: 25,
                    },
                    sort: {
                        field: AssetColumns.bridgedTokenValueDollar,
                        direction: SortDirections.desc,
                    },
                }}
            />
        </div>
    );
}
